import React from "react";

import { FlowVersion } from "src/api/flowTypes";
import {
  DropDown,
  DropDownElement,
  RenderValueFn,
} from "src/base-components/DropDown";
import { FlowVersionStatus } from "src/clients/flow-api";
import {
  getSortedVersions,
  isLastDraftVersion,
  isLastPublishedVersion,
} from "src/flow/modals/utils";
import { VersionPickerDisplay } from "src/flow/modals/VersionPickerDisplay";

export const FromScratchElement = {
  key: "From scratch",
  value: "from-scratch",
} as const;

export type DuplicateVersionOptions =
  | FlowVersion
  | typeof FromScratchElement.value;

export type VersionPickerProps = {
  onChange: (value: string | undefined) => void;
  selected?: string;
  data_loc?: string;
  versions: FlowVersion[];
};

export const VersionPicker: React.FC<VersionPickerProps> = ({
  onChange,
  selected,
  data_loc,
  versions,
}) => {
  const sortedVersions = getSortedVersions(versions);

  const versionElements = sortedVersions.map((version) => ({
    key: version.id,
    value: version,
  }));

  const menuElements: DropDownElement<DuplicateVersionOptions>[] = [
    FromScratchElement,
    ...versionElements,
  ];

  const getSelectedKey = () => {
    if (selected === FromScratchElement.key) {
      return selected;
    }
    const selectedVersions = versions.filter(
      (version) => version.id === selected,
    );
    if (selectedVersions.length > 0) {
      return selectedVersions[0].id;
    }
  };

  const renderButton = (version?: DuplicateVersionOptions) => {
    if (!version) {
      return <></>;
    }
    return (
      <VersionPickerDisplay
        duplicateVersion={version}
        selected={false}
        button
      />
    );
  };

  const includesArchivedVersion = versions.some(
    (version) => version.status === FlowVersionStatus.ARCHIVED,
  );

  const renderValue: RenderValueFn<DuplicateVersionOptions> = ({
    value: duplicateVersion,
    selected,
  }) => {
    return (
      <VersionPickerDisplay
        borderBottom={
          duplicateVersion === FromScratchElement.value ||
          isLastPublishedVersion(versions, duplicateVersion) ||
          (includesArchivedVersion &&
            isLastDraftVersion(versions, duplicateVersion))
        }
        duplicateVersion={duplicateVersion}
        selected={selected}
      />
    );
  };

  return (
    <DropDown<DuplicateVersionOptions>
      buttonDataLoc={data_loc}
      elements={menuElements}
      itemsClassNames="overflow-y-auto max-h-80 decideScrollbar"
      renderButtonValue={renderButton}
      renderValue={renderValue}
      selectedKey={getSelectedKey()}
      onSelect={(selectedKey: string) => onChange(selectedKey)}
    />
  );
};
